import { OrderPayment, RefundReason } from '../../../clients/quipli-service/payment/payment';
import { usePaymentApiV1 } from '../../../clients/quipli-service/payment/payment-api-v1';
import { useState } from 'react';
import { Modal } from '../../../components/modal/modal';
import classNames from 'classnames';
import { Button } from '../../../components/form/button';
import { toast } from '../../../util/toasts';

export const RefundModal = ({
  isOpen,
  close,
  payments,
  refundReasons,
}: {
  isOpen: boolean;
  close: (payment?: OrderPayment) => void;
  payments: OrderPayment[];
  refundReasons: string[];
}) => {
  const { createRefund } = usePaymentApiV1();
  const [payment, setPayment] = useState<OrderPayment>(payments[0]);
  const [amount, setAmount] = useState<string>(payment.balance.toString());
  const [refundReason, setRefundReason] = useState<string>(refundReasons[0]);
  const [comments, setComments] = useState<string>();
  const [isRefunding, setIsRefunding] = useState<boolean>(false);
  const [requiresTerminal, setRequiresTerminal] = useState<boolean>(false);

  async function doRefund() {
    setIsRefunding(true);
    try {
      const result = await createRefund({
        comments: refundReason + (comments ? ` (${comments})` : ''),
        partialAmount: parseFloat(amount),
        paymentIdToRefund: payment.id,
        reason: Object.values(RefundReason).find((r) => r === refundReason) || RefundReason.Other,
      });
      document.getElementsByName('order-payments-table').forEach((element) => {
        const event = new CustomEvent('refresh');
        element.dispatchEvent(event);
      });
      document.getElementsByName('order-invoices').forEach((element) => {
        const event = new CustomEvent('refresh');
        element.dispatchEvent(event);
      });
      toast('Refund issued successfully');
      close(result.payment);
    } catch (ex: any) {
      toast(ex.message, 'error');
    }
    setIsRefunding(false);
  }

  return (
    <Modal
      isOpen={isOpen}
      title="Refund payment"
      additionalText="Refunds take 5-10 days to appear on a customer's statement."
      actions={
        <>
          <Button className="button button-secondary" onClick={() => close()}>
            Cancel
          </Button>
          <Button className="button button-yellow" onClick={() => doRefund()} disabled={isRefunding}>
            Refund
          </Button>
        </>
      }
      close={close}
    >
      <form name="refund-form">
        <div className="flex gap-1 items-center">
          <label className="w-1/3">Amount:</label>
          <input
            className="w-2/3"
            type="text"
            name="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="flex gap-1 items-center">
          <label className="w-1/3">Refund from:</label>
          <select
            name="payment"
            value={payment.id}
            disabled={payments.length <= 1}
            className={classNames('w-2/3', { hidden: payments.length <= 0 })}
            onChange={(e) => {
              setPayment(payments.find((p) => p.id === parseInt(e.target.value))!);
              setRequiresTerminal(
                payments.find((p) => p.id === parseInt(e.target.value))!.gatewayResponse.includes('interac_present'),
              );
            }}
          >
            {payments.map((p) => (
              <option key={p.id} value={p.id}>
                {p.method} {p.reason} ({p.balance})
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-1 items-center">
          <label className="w-1/3">Reason:</label>
          <select
            className="w-2/3"
            disabled={refundReasons.length <= 1}
            name="reason"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
          >
            {refundReasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-1 items-center">
          <label className="w-1/3">Comments:</label>
          <textarea className="w-2/3" value={comments} onChange={(e) => setComments(e.target.value)}></textarea>
        </div>
        {requiresTerminal && (
          <div className="flex gap-1 items-center">
            <label className="w-1/3">Terminal:</label>
            <input className="w-2/3" type="text" name="terminal" />
          </div>
        )}
      </form>
    </Modal>
  );
};
